import { encode as encodeWebP } from '@jsquash/webp';
import { encode as encodeJpeg } from '@jsquash/jpeg';
import { decode as decodePng } from '@jsquash/png';
import { createPNGStreamEncoder } from '../lib/encoder/png';
import { sleep } from '../common-utils';
import { node2HTML } from '../lib/gen/html/index';
import { node2PPT } from '../lib/gen/pptx/ppt';
import {
  DEFAULT_AIGC_METADATA,
  buildAIGCXMP,
  createMetadata,
  injectXMPIntoJPEG,
  injectXMPIntoWebP,
} from '../lib/metadata';
import { sortByIndex } from '../render/utils/node';
import { CacheProvider } from '../provider/cache';
import { CanvasProvider } from '../provider/canvas';
import { DPR, TYPES } from '../render/constants';
import { renderNode } from '../render/node';
import { blobToDataURL } from '../common-utils';
import { Action } from './action';
import { renderNodes, waitForImages } from '../lib/gen/render/render';

export { waitForImages };
export const copyAsPNGAction = new Action({
  name: 'CopyasPNG',
  handler: copyAsPNG,
});

export const copyAsWebpAction = new Action({
  name: 'CopyasWebp',
  handler: copyAsWebp,
});

export const copyAsHTMLAction = new Action({
  name: 'CopyasHTML',
  handler: copyAsHTML,
});

export const copyAsInlineHTMLAction = new Action({
  name: 'CopyAsRawHTML',
  handler: copyAsInlineHTML,
});

async function copyAsPNG(renderContext, options) {
  const nodes = renderContext.api.getSelectedNodes();
  const node = nodes[0];
  if (!node) return;
  if (node.type === TYPES.frame) {
    const result = await exportFrameImage(renderContext, {
      frame: node,
      contentType: 'image/png',
      ...options,
    });
    await writeToClipboard(result, 'image/png');
  } else {
    const result = await renderNodes(renderContext, {
      nodes: nodes,
      contentType: 'image/png',
      ...options,
    });
    await writeToClipboard(result, 'image/png');
  }
}

async function copyAsWebp(renderContext, options) {
  const nodes = renderContext.api.getSelectedNodes();
  const node = nodes[0];
  if (!node) return;
  if (node.type === TYPES.frame) {
    const result = await exportFrameImage(renderContext, {
      frame: node,
      contentType: 'image/webp',
      ...options,
    });
    writeToClipboard(result, 'image/webp');
  } else {
    const result = await renderNodes(renderContext, {
      nodes: nodes,
      contentType: 'image/png',
      ...options,
    });
    writeToClipboard(result, 'image/png');
  }
}

async function copyAsHTML(renderContext, { useHTML = true, useSrc = true } = {}) {
  const nodes = renderContext.api.getSelectedNodes();
  const node = nodes[0];
  if (!node) return;
  let html = '';

  if (nodes.length === 1) {
    html = await node2HTML(renderContext, node, { useHTML, useSrc });
  } else {
    html = await node2HTML(renderContext, nodes, { useHTML, useSrc });
  }
  try {
    await navigator.clipboard.writeText(html);
  } catch (err) {
    console.error('Failed to copy HTML to clipboard:', err);
    throw err;
  }
  return html;
}

async function copyAsInlineHTML(renderContext, _options) {
  const nodes = renderContext.api.getSelectedNodes();
  const node = nodes[0];
  if (!node) return;
  let html = '';

  if (nodes.length === 1) {
    html = await node2HTML(renderContext, node, {
      useInline: true,
      useHTML: true,
      useSrc: true,
    });
  } else {
    html = await node2HTML(renderContext, nodes, {
      useInline: true,
      useHTML: true,
      useSrc: true,
    });
  }
  try {
    await navigator.clipboard.writeText(html);
  } catch (err) {
    console.error('Failed to copy HTML to clipboard:', err);
  }
}

async function writeToClipboard(src, contentType) {
  const blob = await fetch(src).then((r) => r.blob());
  const item = new ClipboardItem({ [contentType]: blob });
  await navigator.clipboard.write([item]);
}

export async function importTemplate(context, template, frameData) {
  if (!template && !template.props) return;
  const nodes = template.props.nodes;
  const list = Object.values(nodes);
  const idsMap = {};

  const addNodes = () => {
    const children = list.filter((n) => n.type !== TYPES.frame);
    children.map((n) => {
      const newId = context.api.createId();
      idsMap[n.id] = newId;
      n.id = newId;
    });
    children.map((n) => {
      if (idsMap[n.parentId]) {
        n.parentId = idsMap[n.parentId];
      }
    });
    children.map((n) => {
      context.api.addNode(n, { useInputId: true });
    });
  };

  const addFrame = (frameData) => {
    // nodes
    const frame = context.api.addFrame({
      ...frameData,
    });
    idsMap[frameData.id] = frame.id;
    context.api.layoutFrame();
    context.api.selectNode([frame.id]);
  };

  if (frameData) {
    addFrame(frameData);
    addNodes();
  } else {
    list
      .filter((n) => n.type === TYPES.frame)
      .sort(sortByIndex)
      .map((frame) => addFrame(frame));
    addNodes();
  }
}

export function isFrameReady(renderContext, frame) {
  const frames = renderContext.api.getFrames();
  const firstFrame = frame || frames[0];
  if (!firstFrame) return true;
  const children = renderContext.api.getChildren(firstFrame.id);
  if (firstFrame.loading && !children.length) return false;

  const images = renderContext.api
    .getDescendants(firstFrame.id)
    .filter((node) => node.type === TYPES.image);

  const nodes = [firstFrame, ...images];
  const hasLoadingImage = nodes.some((node) => {
    return node._img && node._img.loading;
  });

  return !hasLoadingImage;
}

const extContentTypes = {
  png: 'image/png',
  jpg: 'image/jpeg',
  webp: 'image/webp',
  pdf: 'application/pdf',
  tiff: 'image/tiff',
  ppt: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

export function resolveOutputSize(payload, frame) {
  let { printSize, printOptimized = false, dpi, dpr = 1, fast = false } = payload;

  // Validate frame dimensions
  if (!frame || !frame.w || !frame.h || frame.w <= 0 || frame.h <= 0) {
    console.error('Invalid frame dimensions:', frame);
    throw new Error(`Invalid frame dimensions: ${frame?.w}x${frame?.h}`);
  }

  // Validate and clamp dpr
  if (!dpr || dpr <= 0) {
    console.warn('Invalid DPR, using default value of 1:', dpr);
    dpr = 1;
  }

  // Calculate dimensions
  let width, height;
  if (printOptimized && printSize && dpi) {
    // For print-optimized exports, calculate dimensions based on printSize and dpi
    const scaleFactor = dpi / 2.54; // Convert cm to inches and apply DPI
    width = Math.round(printSize.w * scaleFactor);
    height = Math.round(printSize.h * scaleFactor);
    dpr = scaleFactor / (frame.w / printSize.w); // Adjust dpr based on scaling
  } else {
    // Standard export using dpr
    width = Math.round(frame.w * dpr);
    height = Math.round(frame.h * dpr);
  }

  // Ensure dimensions are valid
  width = Math.max(1, width);
  height = Math.max(1, height);
  dpr = Math.max(0.1, dpr);

  return {
    fast,
    width,
    height,
    dpr,
  };
}

async function renderFrame(renderContext, frame, outputSize) {
  const { width, height, dpr, fast } = outputSize;
  return new Promise((resolve, _) => {
    // Standard approach for normal-sized images
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    const exportContext = Object.assign({}, renderContext, {
      context: context,
      dpr,
      mode: fast ? '' : 'export',
      isNodeInViewport: null,
      providers: {
        ...renderContext.providers,
        cache: new CacheProvider(),
        canvas: new CanvasProvider(),
        scale: {
          scale: 1,
        },
      },
    });
    // isolate caches from main render to avoid polluting shared offscreen canvases
    exportContext.providers.cache.attach(exportContext);
    exportContext.providers.canvas.attach(exportContext);

    const scaleProvider = exportContext.providers.scale;
    scaleProvider.scale = dpr;
    scaleProvider.translateX = -frame.x * scaleProvider.scale;
    scaleProvider.translateY = -frame.y * scaleProvider.scale;

    // Apply transformations to position the content correctly
    context.scale(scaleProvider.scale, scaleProvider.scale);
    context.translate(
      scaleProvider.translateX / scaleProvider.scale,
      scaleProvider.translateY / scaleProvider.scale,
    );

    const node = frame;

    // Add slight delay to ensure rendering completes
    setTimeout(() => {
      renderNode(exportContext, node);

      exportContext.providers.cache.dispose();
      exportContext.providers.canvas.dispose();
      // Safari needs time to flush the canvas
      setTimeout(async () => {
        resolve(canvas);
      }, 100);
    }, 100);
  });
}

const PX_TO_MM = 25.4 / 96; // Exact conversion: 1 inch = 25.4mm, 96 DPI standard

// Canvas size below this can be export directly without turning the `willReadFrequently` on
const fastExportCanvasSize = 4096;

// Helper function to initialize JSZip
async function initJSZip() {
  return await import('jszip').then((module) => module.default);
}

// Helper function to initialize jsPDF
async function initJsPDF() {
  const { jsPDF } = await import('jspdf');
  return jsPDF;
}

export function exportFrameImage(renderContext, payload) {
  let {
    frame,
    frames,
    index = 0,
    format = 'png',
    contentType = 'image/png',
    printOptimized = false,
    printSize,
    quality = 1,
    blobMode = false,
    bleedSize = 0,
    callback,
  } = payload;
  const AIGC = createMetadata(payload?.AIGC || DEFAULT_AIGC_METADATA);
  contentType = contentType || extContentTypes[format] || extContentTypes.png;

  const totalPages = frames ? frames.length : 1;
  const report = (progress, page = 1) => {
    if (!callback) return;
    try {
      const clamped = Math.max(0, Math.min(1, progress));
      callback({ progress: clamped, page, total: totalPages });
    } catch {}
  };

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const allFrames = renderContext.api.getFrames();
      frame = frames ? frames[0] : frame || allFrames[index];

      await waitForImages(renderContext, frame);
      await sleep(100);
      await waitForImages(renderContext, frame);

      const outputSize = resolveOutputSize(payload, frame);
      const { width, height, dpr } = outputSize;

      // Handle HTML export

      if (
        contentType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ) {
        const blob = await node2PPT(renderContext, frames, {
          onProgress: (p, i) => report(p, i),
        });
        const zipUrl = URL.createObjectURL(blob);
        resolve(zipUrl);
        return;
      } else if (contentType === 'text/html') {
        if (frames && frames.length > 1) {
          const JSZip = await initJSZip();
          const zip = new JSZip();

          for (let i = 0; i < frames.length; i++) {
            const frame = frames[i];
            const html = await node2HTML(renderContext, frame, { useHTML: true, useSrc: true });
            const htmlDataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
            const blob = await fetch(htmlDataUrl).then((res) => res.blob());
            const extension = contentType.split('/')[1] || 'html';
            const filename = frame.name || `frame-${i + 1}`;
            zip.file(`${filename}.${extension}`, blob);
            report((i + 1) / frames.length, i + 1);
          }

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const zipUrl = URL.createObjectURL(zipBlob);
          resolve(zipUrl);
        } else {
          const html = await node2HTML(renderContext, frame, { useHTML: true, useSrc: true });
          const htmlDataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
          resolve(htmlDataUrl);
        }
        return;
      }

      const isLargeCanvas = width > fastExportCanvasSize || height > fastExportCanvasSize;
      const isPDF = printOptimized || contentType === 'application/pdf';

      // Handle large canvas with tiling
      if (isLargeCanvas) {
        if (isPDF) {
          const jsPDF = await initJsPDF();
          const orientation = frame.w > frame.h ? 'landscape' : 'portrait';
          // Calculate PDF page size: use printSize (in cm) if available, otherwise convert pixels
          const pageWidthMm = printSize ? printSize.w * 10 : frame.w * PX_TO_MM;
          const pageHeightMm = printSize ? printSize.h * 10 : frame.h * PX_TO_MM;
          const pdf = new jsPDF({
            orientation,
            unit: 'mm',
            format: [pageWidthMm + bleedSize * 2, pageHeightMm + bleedSize * 2],
          });

          if (frames && frames.length > 0) {
            for (let i = 0; i < frames.length; i++) {
              const currentFrame = frames[i];
              if (i > 0) {
                const pageOrientation = currentFrame.w > currentFrame.h ? 'landscape' : 'portrait';
                // For multi-frame exports, each frame uses its proportional size
                const framePageWidthMm = printSize
                  ? (currentFrame.w / frame.w) * printSize.w * 10
                  : currentFrame.w * PX_TO_MM;
                const framePageHeightMm = printSize
                  ? (currentFrame.h / frame.h) * printSize.h * 10
                  : currentFrame.h * PX_TO_MM;
                pdf.addPage(
                  [framePageWidthMm + bleedSize * 2, framePageHeightMm + bleedSize * 2],
                  pageOrientation,
                );
              }
              // Calculate the actual px to mm ratio for this frame
              const framePxToMm = printSize ? (printSize.w * 10) / frame.w : PX_TO_MM;
              await renderTiled(renderContext, {
                pdf,
                frame: currentFrame,
                width: currentFrame.w * dpr,
                height: currentFrame.h * dpr,
                dpr,
                bleedSize,
                pxToMm: framePxToMm,
                onProgress: (p) => report((i + p) / frames.length, i + 1),
              });
            }
          } else {
            // Calculate the actual px to mm ratio
            const pxToMm = printSize ? (printSize.w * 10) / frame.w : PX_TO_MM;
            await renderTiled(renderContext, {
              pdf,
              frame,
              width,
              height,
              dpr,
              bleedSize,
              pxToMm,
              onProgress: (p) => report(p, 1),
            });
          }

          const output = blobMode
            ? URL.createObjectURL(pdf.output('blob'))
            : pdf.output('datauristring');
          resolve(output);
        } else {
          if (frames && frames.length > 1) {
            const JSZip = await initJSZip();
            const zip = new JSZip();

            for (let i = 0; i < frames.length; i++) {
              const f = frames[i];
              // Use useBlob: true to avoid blob URL which may fail in Cloudflare Workers
              const blob =
                contentType === 'image/png'
                  ? await renderTiledStreamingPNG(
                      renderContext,
                      {
                        frame: f,
                        width: f.w * dpr,
                        height: f.h * dpr,
                        dpr,
                        onProgress: (p) => report((i + p) / frames.length, i + 1),
                        useBlob: true,
                      },
                      AIGC,
                    )
                  : await renderTiledWithCanvas(
                      renderContext,
                      {
                        frame: f,
                        width: f.w * dpr,
                        height: f.h * dpr,
                        dpr,
                        contentType,
                        onProgress: (p) => report((i + p) / frames.length, i + 1),
                        useBlob: true,
                      },
                      AIGC,
                    );
              const extension = contentType.split('/')[1] || 'png';
              const filename = f.name || `frame-${i + 1}`;
              zip.file(`${filename}.${extension}`, blob);
              report((i + 1) / frames.length, i + 1);
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipUrl = blobMode ? URL.createObjectURL(zipBlob) : await blobToDataURL(zipBlob);
            resolve(zipUrl);
          } else {
            // Use useBlob: true to avoid blob URL which may fail in Cloudflare Workers
            const blob =
              contentType === 'image/png'
                ? await renderTiledStreamingPNG(
                    renderContext,
                    {
                      frame,
                      width: frame.w * dpr,
                      height: frame.h * dpr,
                      dpr,
                      onProgress: (p) => report(p, 1),
                      useBlob: true,
                    },
                    AIGC,
                  )
                : await renderTiledWithCanvas(
                    renderContext,
                    {
                      frame,
                      width: frame.w * dpr,
                      height: frame.h * dpr,
                      dpr,
                      contentType,
                      onProgress: (p) => report(p, 1),
                      useBlob: true,
                    },
                    AIGC,
                  );

            report(1, 1);
            if (blobMode) {
              resolve(URL.createObjectURL(blob));
            } else {
              // Convert blob to data URL
              const dataUrl = await blobToDataURL(blob);
              resolve(dataUrl);
            }
          }
        }
        return;
      }

      // Handle normal-sized canvas
      if (isPDF) {
        const jsPDF = await initJsPDF();
        const orientation = frame.w > frame.h ? 'landscape' : 'portrait';
        // Calculate PDF page size: use printSize (in cm) if available, otherwise convert pixels
        const pageWidthMm = printSize ? printSize.w * 10 : frame.w * PX_TO_MM;
        const pageHeightMm = printSize ? printSize.h * 10 : frame.h * PX_TO_MM;
        const pdf = new jsPDF({
          orientation,
          unit: 'mm',
          format: [pageWidthMm + bleedSize * 2, pageHeightMm + bleedSize * 2],
        });

        if (frames) {
          for (let i = 0; i < frames.length; i++) {
            const frameCanvas = await renderFrame(renderContext, frames[i], outputSize);
            const imgData = frameCanvas.toDataURL('image/jpeg', 1);

            if (i > 0) pdf.addPage();

            pdf.addImage(
              imgData,
              'JPEG',
              bleedSize,
              bleedSize,
              pdf.internal.pageSize.getWidth() - bleedSize * 2,
              pdf.internal.pageSize.getHeight() - bleedSize * 2,
            );

            frameCanvas.width = 0;
            frameCanvas.height = 0;
            report((i + 1) / frames.length, i + 1);
          }
        } else {
          const canvas = await renderFrame(renderContext, frame, outputSize);
          const imgData = canvas.toDataURL('image/jpeg', 1);
          canvas.width = 0;
          canvas.height = 0;
          pdf.addImage(
            imgData,
            'JPEG',
            bleedSize,
            bleedSize,
            pdf.internal.pageSize.getWidth() - bleedSize * 2,
            pdf.internal.pageSize.getHeight() - bleedSize * 2,
          );
          report(1, 1);
        }

        const output = blobMode
          ? URL.createObjectURL(pdf.output('blob'))
          : pdf.output('datauristring');
        resolve(output);
      } else {
        if (frames && frames.length > 1) {
          const JSZip = await initJSZip();
          const zip = new JSZip();

          for (let i = 0; i < frames.length; i++) {
            const currentFrame = frames[i];
            const currentOutputSize = resolveOutputSize(payload, currentFrame);
            const frameCanvas = await renderFrame(renderContext, currentFrame, currentOutputSize);

            let blob = await new Promise((resolve) => {
              frameCanvas.toBlob((blob) => resolve(blob), contentType, quality);
            });

            try {
              blob = await injectMetadata(blob, contentType, frameCanvas, AIGC);
            } catch {}

            const filename = currentFrame.name || `frame-${i + 1}`;
            const entryName = `${filename}.${format}`;
            zip.file(entryName, blob);

            frameCanvas.width = 0;
            frameCanvas.height = 0;
            report((i + 1) / frames.length, i + 1);
          }

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const zipUrl = URL.createObjectURL(zipBlob);
          resolve(zipUrl);
        } else {
          const canvas = await renderFrame(renderContext, frame, outputSize);

          if (!blobMode) {
            let blob = await new Promise((resolve) => {
              canvas.toBlob((blob) => resolve(blob), contentType, quality);
            });
            try {
              blob = await injectMetadata(blob, contentType, canvas, AIGC);
            } catch {}
            const url = await blobToDataURL(blob);
            report(1, 1);
            resolve(url);
          } else {
            canvas.toBlob(
              async (blob) => {
                try {
                  blob = await injectMetadata(blob, contentType, canvas, AIGC);
                } catch {}
                const dataUrl = URL.createObjectURL(blob);
                report(1, 1);
                resolve(dataUrl);
              },
              contentType,
              quality,
            );
          }

          canvas.width = 0;
          canvas.height = 0;
        }
      }
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Convert canvas to target format with metadata
 * For WebP/JPEG: render as PNG first, then transcode
 * @param {HTMLCanvasElement} canvas - Canvas to convert
 * @param {string} contentType - Target content type
 * @param {number} quality - Quality for lossy formats
 * @param {Object} AIGC - AIGC metadata
 * @returns {Promise<Blob>} Blob in target format
 */
async function canvasToFormat(canvas, contentType, quality, AIGC) {
  const { width, height } = canvas;

  // For PNG, use streaming encoder with metadata
  if (contentType === 'image/png') {
    const pngEncoder = createPNGStreamEncoder(width, height, { metadata: AIGC });
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, width, height);
    const row = new Uint8Array(width * 4);
    for (let y = 0; y < height; y++) {
      const start = y * width * 4;
      row.set(imgData.data.subarray(start, start + width * 4));
      pngEncoder.writeRow(row);
    }
    return pngEncoder.end();
  }

  // For WebP/JPEG: render as PNG first, then transcode
  if (contentType === 'image/webp' || contentType === 'image/jpeg') {
    // First, create PNG without metadata (we'll add it after transcoding)
    const pngEncoder = createPNGStreamEncoder(width, height);
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, width, height);
    const row = new Uint8Array(width * 4);
    for (let y = 0; y < height; y++) {
      const start = y * width * 4;
      row.set(imgData.data.subarray(start, start + width * 4));
      pngEncoder.writeRow(row);
    }
    const pngBlob = pngEncoder.end();

    // Transcode PNG blob to target format (returns Blob directly)
    const targetBlob = await transcodePNGToFormat(pngBlob, contentType, quality);

    // Inject metadata into transcoded format
    if (AIGC) {
      const xmp = buildAIGCXMP(AIGC);
      if (contentType === 'image/jpeg') {
        return await injectXMPIntoJPEG(targetBlob, xmp);
      } else if (contentType === 'image/webp') {
        return await injectXMPIntoWebP(targetBlob, xmp);
      }
    }

    return targetBlob;
  }

  // Fallback for other formats
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), contentType, quality);
  });
}

async function injectMetadata(blob, contentType, canvas, AIGC) {
  // Use the new unified conversion function
  return await canvasToFormat(canvas, contentType, 95, AIGC);
}

async function renderTiled(
  renderContext,
  { pdf, frame, width, height, dpr, bleedSize = 0, pxToMm = PX_TO_MM, onProgress },
) {
  // Need to use tiling approach for large images
  const tileSize = 4096; // Use a safe tile size
  // Calculate number of tiles needed
  const tilesX = Math.ceil(width / tileSize);
  const tilesY = Math.ceil(height / tileSize);
  const totalTiles = Math.max(1, tilesX * tilesY);
  let tileIndex = 0;

  // Overlap in mm to prevent white lines between tiles due to floating point precision
  // This overlap ensures adjacent tiles slightly overlap, covering any gaps
  // Use 0.1mm which is sufficient to cover floating point errors while being invisible in print
  const overlapMm = 0.1;

  const cacheProvider = new CacheProvider();
  const canvasProvider = new CanvasProvider();
  const tileCanvas = document.createElement('canvas');

  // Render each tile directly to PDF
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      await sleep(30);

      const tileContext = tileCanvas.getContext('2d', { willReadFrequently: true });

      // Calculate tile dimensions and position
      const tileWidth = Math.min(tileSize, width - tx * tileSize);
      const tileHeight = Math.min(tileSize, height - ty * tileSize);
      const offsetX = (tx * tileSize) / dpr;
      const offsetY = (ty * tileSize) / dpr;

      tileCanvas.width = tileWidth;
      tileCanvas.height = tileHeight;

      const exportContext = Object.assign({}, renderContext, {
        context: tileContext,
        dpr: dpr * DPR,
        mode: 'export',
        isNodeInViewport: null,
        providers: {
          ...renderContext.providers,
          cache: cacheProvider,
          canvas: canvasProvider,
          scale: {
            scale: 1,
          },
        },
      });
      exportContext.providers.cache.attach(exportContext);
      exportContext.providers.canvas.attach(exportContext);

      const scaleProvider = exportContext.providers.scale;
      scaleProvider.scale = dpr;
      scaleProvider.translateX = -(frame.x + offsetX) * dpr;
      scaleProvider.translateY = -(frame.y + offsetY) * dpr;

      tileContext.scale(dpr, dpr);
      tileContext.translate(scaleProvider.translateX / dpr, scaleProvider.translateY / dpr);
      const node = frame;

      // Render the tile
      renderNode(exportContext, node);

      // Add tile to PDF at correct position
      const tileImgData = tileCanvas.toDataURL('image/jpeg', 1);

      // Calculate tile dimensions in mm using the provided pxToMm ratio
      // Use consistent formula: position based on tile index, size based on actual tile pixels
      const tileWidthMm = (tileWidth / dpr) * pxToMm;
      const tileHeightMm = (tileHeight / dpr) * pxToMm;

      // Calculate position using the same formula as dimensions to ensure alignment
      // tile N's left edge = sum of previous tiles' widths = (tx * tileSize / dpr) * pxToMm
      const tileXPos = ((tx * tileSize) / dpr) * pxToMm;
      const tileYPos = ((ty * tileSize) / dpr) * pxToMm;

      // Apply overlap to prevent white lines between tiles due to floating point precision
      // For non-edge tiles, extend slightly to overlap with adjacent tiles
      const xOverlap = tx > 0 ? overlapMm : 0;
      const yOverlap = ty > 0 ? overlapMm : 0;
      const widthExtend = tx < tilesX - 1 ? overlapMm : 0;
      const heightExtend = ty < tilesY - 1 ? overlapMm : 0;

      pdf.addImage(
        tileImgData,
        'JPEG',
        tileXPos + bleedSize - xOverlap,
        tileYPos + bleedSize - yOverlap,
        tileWidthMm + xOverlap + widthExtend,
        tileHeightMm + yOverlap + heightExtend,
      );

      // Clean up tile canvas
      tileCanvas.width = 0;
      tileCanvas.height = 0;
      tileIndex += 1;
      if (onProgress) {
        try {
          onProgress(tileIndex / totalTiles);
        } catch {}
      }
    }
  }

  cacheProvider.dispose();
  canvasProvider.dispose();
}

/**
 * Transcode PNG blob to target format (WebP or JPEG)
 * Uses direct PNG decoding without intermediate canvas for better performance
 * @param {Blob} pngBlob - PNG blob to transcode
 * @param {string} contentType - Target content type ('image/webp' or 'image/jpeg')
 * @param {number} quality - Quality for lossy formats (0-100)
 * @returns {Promise<Blob>} Blob in target format
 */
async function transcodePNGToFormat(pngBlob, contentType, quality = 95) {
  // Directly decode PNG blob to ImageData (no canvas needed!)
  const arrayBuffer = await pngBlob.arrayBuffer();
  const imageData = await decodePng(arrayBuffer);

  // Encode to target format
  if (contentType === 'image/webp') {
    const webpData = await encodeWebP(imageData, { quality });
    return new Blob([webpData], { type: 'image/webp' });
  } else if (contentType === 'image/jpeg') {
    const jpegData = await encodeJpeg(imageData, { quality });
    return new Blob([jpegData], { type: 'image/jpeg' });
  }

  // Fallback: return original PNG
  return pngBlob;
}

async function renderTiledWithCanvas(
  renderContext,
  { frame, width, height, dpr, contentType, onProgress, useBlob },
  AIGC,
) {
  // For WebP and JPEG, render as PNG first then transcode
  if (contentType === 'image/webp' || contentType === 'image/jpeg') {
    const targetContentType = contentType;

    // WebP has a maximum dimension limit of 16383 pixels
    // Adjust DPR if dimensions exceed this limit
    let safeDpr = dpr;
    const maxWebPDimension = 16000;
    const maxDimension = Math.max(width, height);
    if (maxDimension > maxWebPDimension) {
      safeDpr = dpr * (maxWebPDimension / maxDimension);
      console.warn(
        `WebP dimension limit exceeded (${maxDimension}px > ${maxWebPDimension}px). Reducing DPR from ${dpr} to ${safeDpr.toFixed(2)}`,
      );
    }

    // Render as PNG using streaming encoder with safe DPR
    // Use useBlob: true to avoid blob URL which may fail in Cloudflare Workers
    const pngBlob = await renderTiledStreamingPNG(
      renderContext,
      {
        frame,
        width: Math.floor((width * safeDpr) / dpr),
        height: Math.floor((height * safeDpr) / dpr),
        dpr: safeDpr,
        onProgress: (p) => onProgress?.(p * 0.9),
        useBlob: true,
      },
      null, // No AIGC metadata for intermediate PNG
    );

    // Transcode PNG blob to target format
    const targetBlob = await transcodePNGToFormat(pngBlob, targetContentType, 95);

    // Inject metadata into transcoded format
    let finalBlob = targetBlob;

    onProgress?.(1);
    if (useBlob) return finalBlob;
    return URL.createObjectURL(finalBlob);
  }

  // For PNG, use the streaming encoder directly with metadata
  return await renderTiledStreamingPNG(
    renderContext,
    { frame, width, height, dpr, onProgress, useBlob },
    AIGC,
  );
}

async function renderTiledStreamingPNG(
  renderContext,
  { frame, width, height, dpr, onProgress, useBlob },
  AIGC,
) {
  // Validate input dimensions
  if (!width || !height || width <= 0 || height <= 0) {
    throw new Error(`Invalid dimensions for streaming PNG: ${width}x${height}`);
  }
  if (!dpr || dpr <= 0) {
    throw new Error(`Invalid DPR for streaming PNG: ${dpr}`);
  }

  // Stream-encode a huge PNG without allocating a giant buffer
  const tileSize = 4096;
  const stripeHeight = 1024;
  const tilesX = Math.ceil(width / tileSize);

  const encoder = createPNGStreamEncoder(width, height, { metadata: AIGC });

  const rowBuffer = new Uint8Array(width * 4);

  const cacheProvider = new CacheProvider();
  const canvasProvider = new CanvasProvider();
  const tileCanvas = document.createElement('canvas');

  for (let stripeY = 0; stripeY < height; stripeY += stripeHeight) {
    const h = Math.min(stripeHeight, height - stripeY);

    // Render all columns for this horizontal stripe
    const columns = [];
    for (let tx = 0; tx < tilesX; tx++) {
      await sleep(30);
      const w = Math.min(tileSize, width - tx * tileSize);
      const offsetX = (tx * tileSize) / dpr;
      const offsetY = stripeY / dpr;

      tileCanvas.width = w;
      tileCanvas.height = h;
      const tileContext = tileCanvas.getContext('2d');

      const exportContext = Object.assign({}, renderContext, {
        context: tileContext,
        dpr: dpr * DPR,
        mode: 'export',
        isNodeInViewport: null,
        providers: {
          ...renderContext.providers,
          cache: cacheProvider,
          canvas: canvasProvider,
          scale: { scale: 1 },
        },
      });
      const exportCacheProvider = exportContext.providers.cache;
      const exportCanvasProvider = exportContext.providers.canvas;
      exportCacheProvider.attach(exportContext);
      exportCanvasProvider.attach(exportContext);

      const scaleProvider = exportContext.providers.scale;
      scaleProvider.scale = dpr;
      scaleProvider.translateX = -(frame.x + offsetX) * dpr;
      scaleProvider.translateY = -(frame.y + offsetY) * dpr;

      tileContext.scale(dpr, dpr);
      tileContext.translate(scaleProvider.translateX / dpr, scaleProvider.translateY / dpr);

      renderNode(exportContext, frame);

      // Read pixels for this column stripe and release the canvas
      const img = tileContext.getImageData(0, 0, w, h);
      tileCanvas.width = 0;
      tileCanvas.height = 0;

      columns.push({ data: img.data, width: w });
    }

    // Emit rows for this stripe
    for (let ry = 0; ry < h; ry++) {
      let writeOffset = 0;
      for (let tx = 0; tx < columns.length; tx++) {
        const { data, width: cw } = columns[tx];
        const rowStart = ry * cw * 4;
        rowBuffer.set(data.subarray(rowStart, rowStart + cw * 4), writeOffset);
        writeOffset += cw * 4;
      }
      encoder.writeRow(rowBuffer);
      if (onProgress && ry % 32 === 0) {
        try {
          onProgress(Math.max(0, Math.min(1, (stripeY + ry) / height)));
        } catch {}
      }
    }
  }

  cacheProvider.dispose();
  canvasProvider.dispose();

  const blob = encoder.end();
  if (useBlob) return blob;
  return URL.createObjectURL(blob);
}
